import { useState, type ReactNode } from 'react';
import { useMatches } from '@tanstack/react-router';
import { Bell, List, MagnifyingGlass } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { useSession } from '../../lib/session';
import { isStaffRoleName, navForRole, ROLE_TOPBAR } from '../../lib/staff-nav';
import { Icon } from '../Icon';
import { Drawer } from '../ui/dialog';
import { EmptyState } from '../ui/empty-state';
import { IconButton } from '../ui/icon-button';
import { Popover } from '../ui/popover';
import { ProfileMenu } from './profile-menu';
import { StaffNavList } from './staff-nav-list';
import { StaffBannerSlot } from './staff-banner';
import { StaffRail } from './staff-rail';

function NotificationsPopover() {
  return (
    <Popover
      className="w-(--mbm-layout-notifications-width)"
      trigger={(props) => <IconButton icon={Bell} variant="ghost" aria-label="Notifications" {...props} />}
    >
      {() => (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-(--mbm-space-4) pt-(--mbm-space-4)">
            <h2 className="text-label-large text-text-primary">Notifications</h2>
          </div>
          {/* TODO(api): the notifications API is not built yet, so there is nothing to list. */}
          <EmptyState title="You’re all caught up" description="New client activity and tasks will show up here." />
        </div>
      )}
    </Popover>
  );
}

/**
 * Staff app shell (Figma A01.3): a 72px white icon rail that opens to 240px on hover or keyboard focus with
 * role-scoped navigation, a top bar (search, scope, notifications) and the page canvas. Below `lg` the rail
 * becomes a top bar with a slide-in menu.
 */
export function StaffShell({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const role = isStaffRoleName(session?.user.role) ? session.user.role : 'specialist';
  const groups = navForRole(role);
  const topbar = ROLE_TOPBAR[role];
  const matches = useMatches();
  const wide = matches.some((match) => match.staticData?.wide);
  const board = matches.some((match) => match.staticData?.board);

  return (
    <div className="min-h-dvh bg-surface-canvas">
      {/* Reserve the collapsed rail's width; the open rail grows over the content. */}
      <div className="hidden w-(--mbm-layout-rail-width) lg:block" aria-hidden />
      <StaffRail groups={groups} />

      <div className="lg:pl-(--mbm-layout-rail-width)">
        <header className="sticky top-0 z-(--mbm-z-raised) flex h-(--mbm-layout-topbar-height) items-center justify-between gap-(--mbm-space-3) border-b border-border-line bg-surface-card px-(--mbm-space-4) lg:pl-(--mbm-space-7) lg:pr-(--mbm-space-6)">
          <div className="flex min-w-0 flex-1 items-center gap-(--mbm-space-2)">
            <IconButton
              icon={List}
              variant="ghost"
              aria-label="Open menu"
              className="lg:hidden"
              onClick={() => setMenuOpen(true)}
            />
            {/* TODO: global search (⌘K) is not built yet; shown per the design but inert. */}
            <button
              type="button"
              disabled
              aria-label="Search (coming soon)"
              className="flex h-(--mbm-size-button-small) w-full max-w-(--mbm-layout-topbar-search-width) cursor-not-allowed items-center gap-(--mbm-space-2) rounded-full bg-surface-tint px-(--mbm-space-3) text-left text-body-small text-text-tertiary"
            >
              <Icon icon={MagnifyingGlass} size="sm" />
              <span className="flex-1 truncate">{topbar.searchPlaceholder}</span>
              <kbd className="hidden rounded-(--mbm-radius-xs) border border-border-input bg-surface-card px-(--mbm-space-1-5) text-caption sm:block">
                ⌘K
              </kbd>
            </button>
          </div>
          <div className="flex items-center gap-(--mbm-space-2)">
            {/* TODO(api): the scope chip shows a count in the design ("All clients · 142"); no API yet, so label only. */}
            {topbar.scope ? (
              <span className="hidden rounded-full border border-border-default bg-surface-card px-(--mbm-space-2-5) py-(--mbm-space-1) text-label-small text-text-primary sm:inline-block">
                {topbar.scope}
              </span>
            ) : null}
            <NotificationsPopover />
          </div>
        </header>
        <StaffBannerSlot />

        <Drawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title="Menu"
          side="left"
          className="w-(--mbm-layout-rail-expanded-width)"
        >
          <div className="flex h-full flex-col gap-(--mbm-space-4)">
            <nav className="flex-1">
              <StaffNavList groups={groups} collapsed={false} onNavigate={() => setMenuOpen(false)} />
            </nav>
            <ProfileMenu collapsed={false} />
          </div>
        </Drawer>

        <div
          className={cx(
            'mx-auto flex flex-col gap-(--mbm-space-5) px-(--mbm-space-4) py-(--mbm-space-6)',
            // Figma: 84px either side of 1200 at a 1440 window. Wider windows give the extra to the content, so a
            // card or a column is never squeezed onto the 1200 of the design when there is room.
            board
              ? 'max-w-[calc(var(--mbm-layout-staff-wide-max-width)+var(--mbm-layout-staff-board-gutter)*2)] lg:px-(--mbm-layout-staff-board-gutter)'
              : wide
                ? 'max-w-[calc(var(--mbm-layout-staff-wide-max-width)+var(--mbm-layout-staff-gutter)*2)] lg:px-(--mbm-layout-staff-gutter)'
                : 'max-w-[calc(var(--mbm-layout-staff-content-width)+var(--mbm-space-6)*2)] lg:px-(--mbm-space-7)',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
